import {
  OPEN_ITEM_CUSTOM_TYPE,
  REQUESTED_INFO_CUSTOM_TYPE,
} from "constants/chatType";
import { OpenItem, UploadCSV } from "types/Models/openItem";
import { emptyApi } from "./emptyApi";
import { Transaction, Transactions } from "types/Models/books";

export type TransactionWithInformationRequest = Transaction & {
  information_request: string;
};

export type OpenItemFilterParams = {
  search_term?: string;
  start_date?: string;
  end_date?: string;
  min_amount?: string | null;
  max_amount?: string | null;
  cash_flow?: string | null;
  merchants?: string;
  category_ids?: string;
  from_ids?: string;
  journal_entry?: string | boolean | null;
  bank_statement?: string | boolean | null;
  has_comments?: boolean | null;
  has_no_merchants?: boolean | null;
  not_reviewed?: boolean | null;
  has_split_transactions?: boolean | null;
  sort_col?: string;
  sort_order?: string;
};

export type TransactionWithOpenItemInfo = Transactions & {
  transaction: Transaction & {
    information_request: string | null;
    open_item: OpenItem | null;
  };
};

const extendedApi = emptyApi.injectEndpoints({
  endpoints: (build) => ({
    addOpenItems: build.mutation<
      UploadCSV,
      {
        groupId: string;
        file?: File;
        transactionIds?: string;
        channelUrl?: string;
        customType?: string;
        cardStatus?: string;
      }
    >({
      query: ({
        groupId,
        file,
        transactionIds,
        channelUrl,
        customType,
        cardStatus,
      }) => {
        const formData = new FormData();
        if (transactionIds) {
          formData.append("transaction_ids", transactionIds);
        }
        if (file) {
          formData.append("files", file);
        }
        if (channelUrl) {
          formData.append("channel_url", channelUrl);
        }
        if (customType) {
          formData.append("custom_type", customType);
        }
        if (cardStatus) {
          formData.append("card_status", cardStatus);
        }
        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/open-item/`,
          method: "POST",
          body: formData,
        };
      },
    }),

    getOpenItems: build.query<
      UploadCSV,
      { groupId: string; csvId: string } & OpenItemFilterParams
    >({
      query: ({ groupId, csvId, ...filters }) => {
        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/open-item/`,
          params: { csv_id: csvId, ...filters },
        };
      },
      providesTags: () => [{ type: "OPEN_ITEMS" }],
    }),

    getOpenItemTransactions: build.query<
      { transactions: TransactionWithOpenItemInfo[]; total_count: number },
      {
        groupId: string;
        entityId: string;
        csvId?: string;
        transactionIds?: string;
      } & OpenItemFilterParams
    >({
      query: ({ groupId, entityId, csvId, transactionIds, ...filters }) => ({
        url: `/api/inkle/bookkeeping/group/${groupId}/entity/${entityId}/entity_transactions/`,
        params: {
          ...(transactionIds ? { transaction_ids: transactionIds } : {}),
          ...(csvId ? { csv_id: csvId } : {}),
          with_open_item_info: true,
          limit: 10000,
          page_num: 1,
          ...filters,
        },
      }),
      providesTags: () => [{ type: "OPEN_ITEMS_BY_TRANSACTIONS" }],
    }),

    getOpenItemsByTransactionIds: build.query<
      {
        transactions: Array<TransactionWithInformationRequest>;
      },
      { groupId: string; transactionIds: string }
    >({
      query: ({ groupId, transactionIds }) => {
        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/entity_transaction/open-item/?transaction_ids=${transactionIds}`,
        };
      },
      providesTags: () => [{ type: "OPEN_ITEMS_BY_TRANSACTIONS" }],
    }),

    sendClarificationRequest: build.mutation<
      UploadCSV,
      {
        groupId: string;
        channelUrl: string;
        csvId: string;
        customType:
          | typeof REQUESTED_INFO_CUSTOM_TYPE
          | typeof OPEN_ITEM_CUSTOM_TYPE;
      }
    >({
      query: ({ groupId, channelUrl, csvId, customType }) => {
        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/open-item-request/`,
          method: "POST",
          body: {
            channel_url: channelUrl,
            csv_id: csvId,
            custom_type: customType,
          },
        };
      },
    }),

    deleteOpenItemTxn: build.mutation<
      UploadCSV,
      {
        groupId: string;
        openItemId: string;
      }
    >({
      query: ({ groupId, openItemId }) => {
        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/open-item-request/?open_item_id=${openItemId}`,
          method: "DELETE",
        };
      },
      invalidatesTags: () => [
        { type: "OPEN_ITEMS" },
        { type: "OPEN_ITEMS_BY_TRANSACTIONS" },
      ],
    }),

    uploadOpenItemInvoice: build.mutation<
      UploadCSV,
      { groupId: string; openItemId: string; file: File }
    >({
      query: ({ groupId, openItemId, file }) => {
        const formData = new FormData();
        formData.append("open_item_id", openItemId);
        formData.append("file", file);
        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/open-item/`,
          method: "PUT",
          body: formData,
        };
      },
      invalidatesTags: () => [
        { type: "OPEN_ITEMS" },
        { type: "OPEN_ITEMS_BY_TRANSACTIONS" },
      ],
    }),

    deleteOpenItemInvoice: build.mutation<
      UploadCSV,
      { groupId: string; openItemId: string }
    >({
      query: ({ groupId, openItemId }) => {
        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/open-item/?open_item_id=${openItemId}`,
          method: "DELETE",
        };
      },
      invalidatesTags: () => [
        { type: "OPEN_ITEMS" },
        { type: "OPEN_ITEMS_BY_TRANSACTIONS" },
      ],
    }),

    addClarification: build.mutation<
      UploadCSV,
      {
        groupId: string;
        openItemId: string;
        clarification?: string;
        informationRequest?: string;
      }
    >({
      query: ({ groupId, openItemId, clarification, informationRequest }) => {
        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/open-item-request/`,
          method: "PATCH",
          body: {
            open_item_id: openItemId,
            clarification,
            information_request: informationRequest,
          },
        };
      },
      invalidatesTags: () => [{ type: "OPEN_ITEMS" }],
    }),

    bulkAddClarification: build.mutation<
      UploadCSV,
      {
        groupId: string;
        csv_id: string;
        open_items: {
          uuid: string;
          clarification: string;
        }[];
      }
    >({
      query: ({ groupId, csv_id, open_items }) => {
        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/bulk-open-item-request/`,
          method: "PATCH",
          body: {
            open_items: open_items,
            csv_id: csv_id,
          },
        };
      },
      invalidatesTags: () => [{ type: "OPEN_ITEMS" }],
    }),

    submitClarification: build.mutation<
      UploadCSV,
      { groupId: string; messageId: string; csvId: string }
    >({
      query: ({ groupId, messageId, csvId }) => {
        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/open-item-request/`,
          method: "PUT",
          body: {
            csv_id: csvId,
            message_id: messageId,
          },
        };
      },
      invalidatesTags: () => [{ type: "OPEN_ITEMS" }],
    }),
  }),
});

export const {
  useAddOpenItemsMutation,
  useGetOpenItemsQuery,
  useDeleteOpenItemTxnMutation,
  useSendClarificationRequestMutation,
  useUploadOpenItemInvoiceMutation,
  useAddClarificationMutation,
  useSubmitClarificationMutation,
  useDeleteOpenItemInvoiceMutation,
  useGetOpenItemsByTransactionIdsQuery,
  useBulkAddClarificationMutation,
  useGetOpenItemTransactionsQuery,
} = extendedApi;
